import React, { useCallback, useState, useEffect, useRef } from 'react'
import { X, ChevronDown, Loader2 } from 'lucide-react'
import {
    captureAttribution,
    formatAttributionNotes,
    getGtagAttributionParams,
} from '@/lib/attribution'
import { submitBackendLead } from '@/lib/submitToBackend'
import { normalizePhone } from '@/lib/phone'
import { BOOKING_PREFILL_KEY } from '@/lib/bookingPrefill'

interface LeadCaptureModalProps {
    isOpen: boolean
    onClose: () => void
    landingSource?: string
    thankYouPath?: string
    unqualifiedThankYouPath?: string
    skipInterestQuestion?: boolean
    contentMode?: 'agent' | 'guide'
}

interface TrackingWindow extends Window {
    gtag?: (...args: unknown[]) => void
    __activeMetaPixelId?: string
}

type PrimaryInterest = 'simulator' | 'agent' | 'both'
type MessageFrequency =
    | 'almost_none' | 'few_per_week' | 'several_per_day'      // agent form
    | 'none' | 'per_day_1_15' | 'per_day_16_50' | 'per_day_50_plus' // guide form (lead magnet)
type FormStep = 'qualification' | 'name' | 'cabinet' | 'email' | 'phone'

const DEFAULT_COUNTRY = { code: 'ma', dial: '+212', name: 'Maroc' }
const COUNTRIES = [
    DEFAULT_COUNTRY,
    { code: 'fr', dial: '+33', name: 'France' },
    { code: 'be', dial: '+32', name: 'Belgique' },
    { code: 'ch', dial: '+41', name: 'Suisse' },
    { code: 'ca', dial: '+1', name: 'Canada' },
    { code: 'dz', dial: '+213', name: 'Algérie' },
    { code: 'tn', dial: '+216', name: 'Tunisie' },
    { code: 'sn', dial: '+221', name: 'Sénégal' },
    { code: 'ci', dial: '+225', name: "Côte d'Ivoire" },
    { code: 'ae', dial: '+971', name: 'Émirats' },
    { code: 'sa', dial: '+966', name: 'Arabie Saoudite' },
    { code: 'us', dial: '+1', name: 'États-Unis' },
    { code: 'gb', dial: '+44', name: 'Royaume-Uni' },
    { code: 'de', dial: '+49', name: 'Allemagne' },
    { code: 'es', dial: '+34', name: 'Espagne' },
    { code: 'it', dial: '+39', name: 'Italie' },
    { code: 'nl', dial: '+31', name: 'Pays-Bas' },
]

const PRIMARY_INTEREST_OPTIONS: { value: PrimaryInterest; label: string }[] = [
    { value: 'simulator', label: 'Le simulateur de sourire (avant/après patient)' },
    { value: 'agent',     label: "L'agent IA (WhatsApp, Instagram, Messenger)" },
    { value: 'both',      label: 'Les deux' },
]
const MESSAGE_FREQUENCY_OPTIONS: { value: MessageFrequency; label: string }[] = [
    { value: 'almost_none',    label: 'Presque aucun' },
    { value: 'few_per_week',   label: 'Quelques-uns par semaine' },
    { value: 'several_per_day', label: 'Plusieurs par jour' },
]

const GUIDE_FREQUENCY_QUESTION = 'En moyenne, combien de messages de patients votre centre reçoit-il par jour sur WhatsApp, Instagram et Messenger, hors appels ?'
const GUIDE_FREQUENCY_OPTIONS: { value: MessageFrequency; label: string }[] = [
    { value: 'none',            label: 'Aucun — nous recevons principalement des appels' },
    { value: 'per_day_1_15',    label: 'De 1 à 15 messages par jour' },
    { value: 'per_day_16_50',   label: 'De 16 à 50 messages par jour' },
    { value: 'per_day_50_plus', label: 'Plus de 50 messages par jour' },
]

// Backend buckets stay the same; the exact answer goes in the notes
function mapFrequencyToVolume(f: MessageFrequency | null): string {
    if (f === 'several_per_day' || f === 'per_day_50_plus') return '100+'
    if (f === 'few_per_week' || f === 'per_day_16_50') return '50-100'
    return '0-50'
}

function isUnqualifiedLead(interest: PrimaryInterest | null, frequency: MessageFrequency | null): boolean {
    return (interest === 'agent' || interest === 'both') && (frequency === 'almost_none' || frequency === 'none')
}

const FlagImg: React.FC<{ code: string }> = ({ code }) => (
    <img
        src={`https://flagcdn.com/w40/${code}.png`}
        srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
        alt=""
        style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2, display: 'block' }}
        loading="lazy"
    />
)

/* ── Design tokens ──────────────────────────────────────────── */
const PURPLE = '#6941E8'
const INPUT_BG = '#FFFFFF'
const FONT = "'Inter', system-ui, -apple-system, sans-serif"

/* ── Shared styles ──────────────────────────────────────────── */
const S = {
    card: {
        position: 'relative' as const,
        width: '100%',
        maxWidth: 480,
        margin: '0 auto',
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 24px 80px rgba(0,0,0,0.22)',
        display: 'flex' as const,
        flexDirection: 'column' as const,
        maxHeight: '100dvh',
        overflow: 'hidden' as const,
    },
    body: {
        padding: '40px 36px 36px',
        overflowY: 'auto' as const,
        WebkitOverflowScrolling: 'touch' as const,
        flexGrow: 1,
    },
    /* Heading: thin + bold keyword */
    h: (thin: string, bold: string, after = '') => (
        <h2 style={{ fontFamily: FONT, fontSize: 'clamp(26px, 5vw, 32px)', fontWeight: 400, color: '#111', textAlign: 'center', margin: '0 0 32px', lineHeight: 1.22 }}>
            {thin} <strong style={{ fontWeight: 800 }}>{bold}</strong>{after}
        </h2>
    ),
    label: { display: 'block' as const, fontSize: 13, fontWeight: 600, color: '#888', marginBottom: 7, fontFamily: FONT },
    input: {
        width: '100%',
        height: 50,
        background: INPUT_BG,
        border: '1px solid #DDE1EA',
        borderRadius: 12,
        padding: '0 12px',
        fontSize: 16,
        fontFamily: FONT,
        fontWeight: 500,
        letterSpacing: '0',
        color: '#0A1744',
        outline: 'none',
        boxSizing: 'border-box' as const,
        transition: 'border-color .15s, box-shadow .15s',
    },
    btn: {
        width: '100%',
        height: 52,
        borderRadius: 12,
        background: PURPLE,
        border: 'none',
        color: '#fff',
        fontFamily: FONT,
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: '0',
        textTransform: 'none' as const,
        cursor: 'pointer',
        display: 'flex' as const,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        gap: 8,
        marginTop: 28,
        transition: 'filter .15s',
    },
    prevBtn: {
        width: '100%',
        background: 'none',
        border: 'none',
        color: '#111',
        fontFamily: FONT,
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        marginTop: 14,
        padding: '4px 0',
        transition: 'color .15s',
    },
}

/* ── Simple radio row ───────────────────────────────────────── */
const RadioOption: React.FC<{ label: string; selected: boolean; onClick: () => void }> = ({ label, selected, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '6px 0',
            fontFamily: FONT,
            textAlign: 'left' as const,
            width: '100%',
        }}
    >
        {/* Radio circle */}
        <span style={{
            width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
            border: `2px solid ${selected ? PURPLE : '#c4c4c4'}`,
            background: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'border-color .12s',
        }}>
            {selected && (
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: PURPLE, display: 'block' }} />
            )}
        </span>
        <span style={{ fontSize: 15, color: selected ? '#111' : '#374151', fontWeight: selected ? 600 : 400, lineHeight: 1.35 }}>
            {label}
        </span>
    </button>
)

/* ── Field label with red asterisk ─────────────────────────── */
const FieldLabel: React.FC<{ text: string }> = ({ text }) => (
    <label style={S.label}>
        {text}<span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>
    </label>
)

/* ── Main component ─────────────────────────────────────────── */
export const LeadCaptureModal: React.FC<LeadCaptureModalProps> = ({
    isOpen,
    onClose,
    landingSource = 'doctors-agent',
    thankYouPath = '/doctors-agent/merci',
    unqualifiedThankYouPath = '/doctors-agent/merci-non-qualifie',
    skipInterestQuestion = false,
    contentMode = 'agent',
}) => {
    const isGuideFlow = contentMode === 'guide'
    const frequencyOptions = isGuideFlow ? GUIDE_FREQUENCY_OPTIONS : MESSAGE_FREQUENCY_OPTIONS
    const [step, setStep] = useState<FormStep>('qualification')
    const [interest, setInterest] = useState<PrimaryInterest | null>(skipInterestQuestion ? 'agent' : null)
    const [frequency, setFrequency] = useState<MessageFrequency | null>(null)
    const [name, setName] = useState('')
    const [cabinet, setCabinet] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [phoneError, setPhoneError] = useState('')
    const [country, setCountry] = useState(DEFAULT_COUNTRY)
    const [showPicker, setShowPicker] = useState(false)
    const [isWhatsApp, setIsWhatsApp] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [fieldError, setFieldError] = useState('')
    const [phoneFocused, setPhoneFocused] = useState(false)

    const inputRef = useRef<HTMLInputElement>(null)
    // Set synchronously: Enter in the phone field + a click on the button must not submit twice
    const submittingRef = useRef(false)
    const countryRef = useRef<HTMLDivElement>(null)
    const bodyRef = useRef<HTMLDivElement>(null)
    const q2Ref = useRef<HTMLDivElement>(null)

    const reset = useCallback(() => {
        setStep('qualification'); setInterest(skipInterestQuestion ? 'agent' : null); setFrequency(null)
        setName(''); setCabinet(''); setEmail(''); setPhone('')
        setPhoneError(''); setFieldError(''); setCountry(DEFAULT_COUNTRY)
        setShowPicker(false); setIsWhatsApp(true); setIsSubmitting(false); setPhoneFocused(false)
    }, [skipInterestQuestion])

    useEffect(() => { if (isOpen) { captureAttribution(); reset() } }, [isOpen, reset])

    useEffect(() => {
        if (isOpen) document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = '' }
    }, [isOpen])

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0
        setFieldError('')
        const t = window.setTimeout(() => inputRef.current?.focus(), 180)
        return () => window.clearTimeout(t)
    }, [step])

    /* Scroll Q2 into view when it appears */
    useEffect(() => {
        if (!skipInterestQuestion && interest && interest !== 'simulator' && q2Ref.current) {
            setTimeout(() => q2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80)
        }
    }, [interest, skipInterestQuestion])

    useEffect(() => {
        const h = (e: MouseEvent) => {
            if (countryRef.current && !countryRef.current.contains(e.target as Node)) setShowPicker(false)
        }
        document.addEventListener('mousedown', h)
        return () => document.removeEventListener('mousedown', h)
    }, [])

    const validatePhone = (v: string) => {
        const d = v.replace(/\D/g, '')
        if (!d) { setPhoneError('Numéro requis'); return false }
        if (d.length < 6) { setPhoneError('Numéro trop court'); return false }
        setPhoneError(''); return true
    }

    /* Is the "Continuer" button on the qualification step enabled? */
    const qualificationComplete =
        interest === 'simulator' ||
        (interest !== null && frequency !== null)

    const goBack = () => {
        if (step === 'name') setStep('qualification')
        else if (step === 'cabinet') setStep('name')
        else if (step === 'email') setStep('cabinet')
        else if (step === 'phone') setStep('email')
    }

    const handleContinue = () => {
        setFieldError('')
        if (step === 'qualification') {
            if (qualificationComplete) setStep('name')
        } else if (step === 'name') {
            if (!name.trim()) { setFieldError('Ce champ est requis'); return }
            setStep('cabinet')
        } else if (step === 'cabinet') {
            if (!cabinet.trim()) { setFieldError('Ce champ est requis'); return }
            setStep('email')
        } else if (step === 'email') {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setFieldError('Email invalide'); return }
            setStep('phone')
        } else if (step === 'phone') {
            if (validatePhone(phone)) handleSubmit()
        }
    }

    const handleSubmit = async () => {
        if (!interest || submittingRef.current) return
        submittingRef.current = true
        setIsSubmitting(true)
        try {
            const interestLabel = PRIMARY_INTEREST_OPTIONS.find(o => o.value === interest)?.label ?? interest
            const freqLabel = frequencyOptions.find(o => o.value === frequency)?.label ?? 'N/A'
            const unqualified = isUnqualifiedLead(interest, frequency)
            await submitBackendLead({
                landingSource,
                name,
                phone: normalizePhone(phone, country.dial),
                email,
                messageVolume: mapFrequencyToVolume(frequency),
                notes: `[QUALIFICATION]\nIntérêt: ${interestLabel}\nVolume messages: ${freqLabel}\nStatut: ${unqualified ? (isGuideFlow ? 'NON QUALIFIÉ (aucun message, surtout des appels)' : 'NON QUALIFIÉ (agent IA + presque aucun message)') : 'QUALIFIÉ'}\n\nCabinet: ${cabinet}\nEmail: ${email}\n\nWhatsApp: ${isWhatsApp ? 'OUI' : 'NON'}\nPays: ${country.name} (${country.dial})${formatAttributionNotes()}`,
            })
            if (isGuideFlow) {
                // Prefill the booking calendar on the thank-you page
                try {
                    sessionStorage.setItem(BOOKING_PREFILL_KEY, JSON.stringify({
                        name,
                        phone: normalizePhone(phone, country.dial),
                        cabinet,
                    }))
                } catch { /* storage unavailable: the doctor types them again */ }
            }
            const trackingWindow = window as TrackingWindow
            trackingWindow.gtag?.('event', 'lead_submit', { event_category: 'Form', event_label: 'Doctors Agent', ...getGtagAttributionParams() })
            const qualified = !isUnqualifiedLead(interest, frequency)
            const redirectPath = qualified ? thankYouPath : unqualifiedThankYouPath
            // Set one-time flag so the qualified thank-you page fires Lead exactly once
            if (qualified && trackingWindow.__activeMetaPixelId) {
                sessionStorage.setItem('da_lead_pending', trackingWindow.__activeMetaPixelId)
            }
            setIsSubmitting(false); onClose(); window.location.href = redirectPath
        } catch (error: unknown) {
            setIsSubmitting(false)
            submittingRef.current = false
            const message = error instanceof Error ? error.message : 'Une erreur est survenue'
            alert(`Erreur: ${message}`)
        }
    }

    if (!isOpen) return null

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.currentTarget.style.borderColor = PURPLE
        e.currentTarget.style.boxShadow = '0 0 0 4px rgba(105,65,232,.12)'
        e.currentTarget.style.background = '#fff'
    }
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        e.currentTarget.style.borderColor = '#DDE1EA'
        e.currentTarget.style.boxShadow = 'none'
        e.currentTarget.style.background = INPUT_BG
    }

    return (
        <div
            style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: FONT }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
        >
            {/* Backdrop */}
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }} onClick={onClose} />

            {/* Card */}
            <div style={S.card} className="animate-in zoom-in-95 fade-in duration-200">

                {/* Close button */}
                <button
                    onClick={onClose}
                    aria-label="Fermer"
                    style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: '50%', border: '2px solid #ccc', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}
                >
                    <X size={14} color="#555" />
                </button>

                {/* Body */}
                <div ref={bodyRef} style={S.body}>

                    {/* ══ STEP: qualification ══════════════════════════════════ */}
                    {step === 'qualification' && (
                        <>
                            {/* Bold centred heading like the reference */}
                            <h2 style={{ fontFamily: FONT, fontSize: 'clamp(18px, 4.5vw, 22px)', fontWeight: 800, color: '#111', textAlign: 'center', margin: '0 0 28px', lineHeight: 1.3 }}>
                                {isGuideFlow ? 'Une question rapide avant l’envoi' : 'Deux questions rapides avant votre démo'}
                            </h2>

                            {/* Q1 */}
                            {!skipInterestQuestion && (
                                <>
                                    <p style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 10px' }}>
                                        Qu'est-ce qui vous intéresse le plus ?
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 24 }}>
                                        {PRIMARY_INTEREST_OPTIONS.map(({ value, label }) => (
                                            <RadioOption
                                                key={value}
                                                label={label}
                                                selected={interest === value}
                                                onClick={() => {
                                                    setInterest(value)
                                                    if (value === 'simulator') setFrequency(null)
                                                }}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* Q2, appears dynamically when agent/both is selected */}
                            {(skipInterestQuestion || (interest !== null && interest !== 'simulator')) && (
                                <div ref={q2Ref} style={{ animation: 'fadeSlideIn .22s ease' }}>
                                    <p style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 10px' }}>
                                        {isGuideFlow ? GUIDE_FREQUENCY_QUESTION : 'Combien de messages recevez-vous sur WhatsApp, Instagram et Facebook ?'}
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
                                        {frequencyOptions.map(({ value, label }) => (
                                            <RadioOption
                                                key={value}
                                                label={label}
                                                selected={frequency === value}
                                                onClick={() => setFrequency(value)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Continue, only enabled when complete */}
                            <button
                                style={{ ...S.btn, opacity: qualificationComplete ? 1 : 0.4, cursor: qualificationComplete ? 'pointer' : 'default' }}
                                type="button"
                                disabled={!qualificationComplete}
                                onClick={handleContinue}
                                onMouseEnter={e => { if (qualificationComplete) e.currentTarget.style.filter = 'brightness(1.1)' }}
                                onMouseLeave={e => { e.currentTarget.style.filter = 'none' }}
                            >
                                Continuer
                            </button>
                        </>
                    )}

                    {/* ══ STEP: name ═══════════════════════════════════════════ */}
                    {step === 'name' && <>
                        {isGuideFlow ? S.h("À qui envoyons-nous", "le guide", " ?") : S.h("Quel est votre", "nom", " ?")}
                        <FieldLabel text="Nom complet" />
                        <input ref={inputRef} style={S.input} type="text" value={name} placeholder="VOTRE NOM" autoComplete="name"
                            onChange={e => { setName(e.target.value.replace(/[0-9]/g, '')); setFieldError('') }}
                            onKeyDown={e => e.key === 'Enter' && handleContinue()}
                            onFocus={handleFocus} onBlur={handleBlur}
                        />
                        {fieldError && <p style={{ color: '#ef4444', fontSize: 13, margin: '6px 0 0', fontFamily: FONT }}>{fieldError}</p>}
                        <button style={S.btn} type="button" onClick={handleContinue}
                            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)' }}
                            onMouseLeave={e => { e.currentTarget.style.filter = 'none' }}>
                            Continuer
                        </button>
                        <button style={S.prevBtn} onMouseEnter={e => (e.currentTarget.style.color = '#555')} onMouseLeave={e => (e.currentTarget.style.color = '#111')} onClick={goBack}>← Précédent</button>
                    </>}

                    {/* ══ STEP: cabinet ════════════════════════════════════════ */}
                    {step === 'cabinet' && <>
                        {isGuideFlow ? S.h("Pour quel", "cabinet", " ?") : S.h("Nom de votre", "cabinet", " ?")}
                        <FieldLabel text="Nom du cabinet" />
                        <input ref={inputRef} style={S.input} type="text" value={cabinet} placeholder="NOM DU CABINET" autoComplete="organization"
                            onChange={e => { setCabinet(e.target.value); setFieldError('') }}
                            onKeyDown={e => e.key === 'Enter' && handleContinue()}
                            onFocus={handleFocus} onBlur={handleBlur}
                        />
                        {fieldError && <p style={{ color: '#ef4444', fontSize: 13, margin: '6px 0 0', fontFamily: FONT }}>{fieldError}</p>}
                        <button style={S.btn} type="button" onClick={handleContinue}
                            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)' }}
                            onMouseLeave={e => { e.currentTarget.style.filter = 'none' }}>
                            Continuer
                        </button>
                        <button style={S.prevBtn} onMouseEnter={e => (e.currentTarget.style.color = '#555')} onMouseLeave={e => (e.currentTarget.style.color = '#111')} onClick={goBack}>← Précédent</button>
                    </>}

                    {/* ══ STEP: email ══════════════════════════════════════════ */}
                    {step === 'email' && <>
                        {isGuideFlow ? S.h("Votre adresse", "email", " ?") : S.h("Votre", "email", " professionnel ?")}
                        <FieldLabel text="Adresse email" />
                        <input ref={inputRef} style={S.input} type="email" value={email} placeholder="ADRESSE EMAIL" autoComplete="email"
                            onChange={e => { setEmail(e.target.value); setFieldError('') }}
                            onKeyDown={e => e.key === 'Enter' && handleContinue()}
                            onFocus={handleFocus} onBlur={handleBlur}
                        />
                        {fieldError && <p style={{ color: '#ef4444', fontSize: 13, margin: '6px 0 0', fontFamily: FONT }}>{fieldError}</p>}
                        <button style={S.btn} type="button" onClick={handleContinue}
                            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)' }}
                            onMouseLeave={e => { e.currentTarget.style.filter = 'none' }}>
                            Continuer
                        </button>
                        <button style={S.prevBtn} onMouseEnter={e => (e.currentTarget.style.color = '#555')} onMouseLeave={e => (e.currentTarget.style.color = '#111')} onClick={goBack}>← Précédent</button>
                    </>}

                    {/* ══ STEP: phone ══════════════════════════════════════════ */}
                    {step === 'phone' && <>
                        {isGuideFlow ? S.h("Votre numéro", "WhatsApp", " ?") : S.h("Votre numéro de", "téléphone", " ?")}
                        <FieldLabel text={isGuideFlow ? 'Numéro WhatsApp' : 'Meilleur numéro'} />

                        {/* Unified phone field */}
                        <div ref={countryRef} style={{ position: 'relative' }}>
                            <div style={{
                                display: 'flex', alignItems: 'center',
                                background: '#fff',
                                border: `1px solid ${phoneError ? '#C6353A' : phoneFocused ? PURPLE : '#DDE1EA'}`,
                                boxShadow: phoneFocused && !phoneError ? '0 0 0 4px rgba(105,65,232,.12)' : 'none',
                                borderRadius: 12, height: 50,
                                transition: 'border-color .15s, background .15s',
                            }}>
                                <button type="button" onClick={() => setShowPicker(!showPicker)}
                                    style={{ flexShrink: 0, height: '100%', padding: '0 10px 0 14px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                                    <FlagImg code={country.code} />
                                    <ChevronDown size={11} color="#9ca3af" />
                                </button>
                                <span style={{ width: 1, height: 24, background: '#d1d5db', flexShrink: 0 }} />
                                <span style={{ padding: '0 8px 0 10px', color: '#6b7280', fontSize: 13, fontFamily: FONT, whiteSpace: 'nowrap', userSelect: 'none' }}>{country.dial}</span>
                                <input ref={inputRef} type="tel" value={phone} placeholder="" autoComplete="tel"
                                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 15, fontFamily: FONT, fontWeight: 600, letterSpacing: '0.02em', color: '#111', height: '100%', paddingRight: 14 }}
                                    onChange={e => { setPhone(e.target.value); if (e.target.value) validatePhone(e.target.value); else setPhoneError('') }}
                                    onFocus={() => setPhoneFocused(true)}
                                    onBlur={() => { setPhoneFocused(false); if (phone) validatePhone(phone) }}
                                    onKeyDown={e => e.key === 'Enter' && handleContinue()}
                                />
                            </div>

                            {showPicker && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, width: '100%', maxHeight: 210, overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.14)', zIndex: 50 }}>
                                    {COUNTRIES.map(c => (
                                        <button key={c.code} type="button" onClick={() => { setCountry(c); setShowPicker(false) }}
                                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: country.code === c.code ? '#f5f3ff' : 'transparent', border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: country.code === c.code ? 600 : 400 }}>
                                            <FlagImg code={c.code} />
                                            <span style={{ color: '#374151', flex: 1, textAlign: 'left' }}>{c.name}</span>
                                            <span style={{ color: '#bbb', fontSize: 11 }}>{c.dial}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {phoneError && <p style={{ color: '#ef4444', fontSize: 13, margin: '6px 0 0', fontFamily: FONT }}>{phoneError}</p>}

                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer', userSelect: 'none' }}>
                            <input type="checkbox" checked={isWhatsApp} onChange={e => setIsWhatsApp(e.target.checked)} style={{ width: 15, height: 15, accentColor: '#22c55e' }} />
                            <span style={{ fontSize: 12, color: '#aaa', fontFamily: FONT }}>
                                {isGuideFlow ? 'Ce numéro est bien sur WhatsApp' : 'Ce numéro est sur WhatsApp'}
                            </span>
                        </label>

                        <button style={{ ...S.btn, opacity: isSubmitting ? 0.7 : 1 }} type="button" disabled={isSubmitting} onClick={handleContinue}
                            onMouseEnter={e => { if (!isSubmitting) e.currentTarget.style.filter = 'brightness(1.1)' }}
                            onMouseLeave={e => { e.currentTarget.style.filter = 'none' }}>
                            {isSubmitting ? (
                                <><Loader2 size={16} className="animate-spin" /> {isGuideFlow ? 'Envoi du guide…' : 'Envoi…'}</>
                            ) : isGuideFlow ? (
                                <>
                                    Recevoir mon guide gratuitement
                                    <img src="/icons/whatsapp.svg" alt="" aria-hidden style={{ width: 17, height: 17, filter: 'brightness(0) invert(1)' }} />
                                </>
                            ) : 'Je veux tester 7 jours →'}
                        </button>

                        <button style={S.prevBtn} onMouseEnter={e => (e.currentTarget.style.color = '#555')} onMouseLeave={e => (e.currentTarget.style.color = '#111')} onClick={goBack}>← Précédent</button>
                    </>}

                </div>

                {/* Trust bar */}
                <div style={{ flexShrink: 0, background: '#1a1d2e', borderRadius: '0 0 20px 20px', padding: '14px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6L12 2z" fill={PURPLE} opacity=".3" />
                            <path d="M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6L12 2z" stroke={PURPLE} strokeWidth="1.5" strokeLinejoin="round" />
                            <path d="M9 12l2 2 4-4" stroke={PURPLE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.15)', display: 'inline-block' }} />
                        <div style={{ display: 'flex', gap: 3 }}>
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b" aria-hidden>
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                </svg>
                            ))}
                        </div>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, margin: 0, fontFamily: FONT, letterSpacing: '0.01em' }}>
                        {isGuideFlow ? 'Vos données sont sécurisées · Envoi immédiat sur WhatsApp · ' : 'Vos données 100% sécurisées · Rappel sous 24 h. · '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}>
                            Politique de confidentialité
                        </a>
                    </p>
                </div>

            </div>

            {/* Keyframe for Q2 fade-in */}
            <style>{`
                @keyframes fadeSlideIn {
                    from { opacity: 0; transform: translateY(-8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    )
}
